import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  cleared: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      cleared: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleClearAndReload = async () => {
    try {
      // Clear all local storage and session storage
      localStorage.clear();
      sessionStorage.clear();

      // Clear Supabase session via direct fetch to avoid import issues
      const supabaseUrl = (window as any).__SUPABASE_URL__ || '';
      if (supabaseUrl) {
        await fetch(`${supabaseUrl}/auth/v1/logout`, {
          method: 'POST',
          credentials: 'include',
        }).catch(() => {});
      }
    } catch {
      // Ignore errors during cleanup
    } finally {
      // Redirect to home and force full reload
      window.location.href = '/';
    }
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '2rem',
          textAlign: 'center',
          background: '#07070D',
          color: '#fff',
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
          <h1 style={{ fontSize: '1.5rem', marginBottom: '0.75rem', fontWeight: 'bold' }}>
            Ops! Algo deu errado
          </h1>
          <p style={{ marginBottom: '2rem', color: '#888', maxWidth: '400px', lineHeight: 1.6 }}>
            Ocorreu um erro inesperado. Clique em "Limpar e recarregar" para resolver.
          </p>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}>
            <button
              onClick={this.handleClearAndReload}
              style={{
                padding: '0.75rem 2rem',
                backgroundColor: '#D4AF37',
                color: '#07070D',
                border: 'none',
                borderRadius: '0.75rem',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 'bold',
              }}
            >
              Limpar e recarregar
            </button>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '0.75rem 2rem',
                backgroundColor: 'transparent',
                color: '#888',
                border: '1px solid #333',
                borderRadius: '0.75rem',
                cursor: 'pointer',
                fontSize: '1rem',
              }}
            >
              Tentar novamente
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;